import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

// third party
import { useForm } from 'react-hook-form';

// project import
import { buttonStyle } from 'components/buttons/buttonStyle';
import Loader from 'components/Loader';
import { Button } from 'components/ui/button';
import { FieldError } from 'components/ui/field';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import useAuth from 'hooks/useAuth';
import useScriptRef from 'hooks/useScriptRef';
import { successAlert } from 'utils/alert';
import { strengthColor, strengthIndicator } from 'utils/password-strength';

// types
import { StringColorProps } from 'types/password';

import { cn } from 'utils/cn';
import { cognitoUser } from 'utils/cognitoUser';
import { t } from 'utils/getTranslations';
import { isValidPassword } from 'utils/validators';
import { PasswordInput } from './PasswordInput';

// ============================|| STATIC - RESET PASSWORD ||============================ //

interface SetPasswordValues {
    password: string;
    confirmPassword: string;
}

const AuthSetPassword = () => {
    const scriptedRef = useScriptRef();
    const navigate = useNavigate();

    const { login, setInitialPassword } = useAuth();

    const [searchParams] = useSearchParams();
    // The onboarding email links here with the temporary credentials embedded, so the user is
    // not asked to re-type them.
    const activationEmail = searchParams.get('email');
    const activationTempPassword = searchParams.get('tempPassword');
    const hasChallengeSession = Boolean(cognitoUser.getCognitoUser() && cognitoUser.getSessionUserAttributes());

    const [level, setLevel] = useState<StringColorProps | undefined>(() => strengthColor(strengthIndicator('')));
    const [showPassword, setShowPassword] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    // oxlint-disable-next-line react-doctor/rerender-state-only-in-handlers -- read in the render gate below to swap the loader for the error message, so the sign-in failure must re-render
    const [autoSignInFailed, setAutoSignInFailed] = useState(false);
    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const changePassword = (value: string) => {
        const temp = strengthIndicator(value);
        setLevel(strengthColor(temp));
    };

    useEffect(() => {
        // Reached with a live NEW_PASSWORD_REQUIRED challenge (normal login flow) — show the form.
        if (hasChallengeSession) {
            return;
        }
        // Reached from the onboarding email: sign in once with the embedded temporary credentials.
        // Cognito answers with the NEW_PASSWORD_REQUIRED challenge, which navigates back here (now
        // with a session) so the user only has to pick a permanent password.
        if (activationEmail && activationTempPassword) {
            void login(activationEmail, activationTempPassword).catch(() => setAutoSignInFailed(true));
            // Scrub the temporary credentials from the address bar/history now that they are
            // consumed. Done via the History API rather than React Router so the in-memory search
            // params still gate the loader below until the challenge navigation replaces them.
            window.history.replaceState(null, '', window.location.pathname);
            return;
        }
        // No challenge and nothing to sign in with — nobody should be on this page.
        navigate('/login', { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot mount bootstrap; re-running on param/session changes would re-trigger the sign-in
    }, []);

    const {
        register,
        handleSubmit,
        setValue,
        getValues,
        formState: { errors, isSubmitting },
    } = useForm<SetPasswordValues>({
        mode: 'onBlur',
        defaultValues: { password: '', confirmPassword: '' },
    });

    const onSubmit = async (values: SetPasswordValues) => {
        setSubmitError(null);
        const password = values.password?.trim?.() ?? values.password;
        try {
            if (scriptedRef.current) {
                const status = await setInitialPassword(password);
                successAlert('Successfully reset password.');
                // The backend marks the email verified when it opens the portal session, which happens
                // here or after the enrolment it asks for.
                navigate(status === 'mfaSetupRequired' ? '/mfa-setup' : '/', { replace: true });
            }
        } catch (err) {
            console.error(err);
            if (scriptedRef.current && err instanceof Error) {
                setSubmitError(err.message);
            }
        }
    };

    const passwordRegister = register('password', {
        required: t('auth.password-required'),
        validate: async (value) => {
            const res = await isValidPassword(value, t('auth.invalid-password')).catch(() => null);
            return res?.isValid ? true : t('auth.password-requirements');
        },
    });
    const confirmPasswordRegister = register('confirmPassword', {
        required: t('auth.password-required'),
        validate: (value) => value === getValues('password') || t('auth.passwords-must-match'),
    });

    // Signing in with the embedded temporary credentials before the password form can be shown.
    if (activationEmail && activationTempPassword && !hasChallengeSession) {
        if (autoSignInFailed) {
            return <FieldError>{t('auth.activation-link-invalid')}</FieldError>;
        }
        return <Loader />;
    }

    return (
        <form
            onSubmit={(e) => {
                void handleSubmit(onSubmit)(e);
            }}
            noValidate
        >
            <div className="flex flex-col gap-6">
                <div>
                    <div className="flex flex-col gap-2">
                        <Label htmlFor="password-reset">New Password</Label>
                        <PasswordInput
                            id="password-reset"
                            placeholder="Enter password"
                            autoComplete="new-password"
                            aria-invalid={Boolean(errors.password)}
                            visible={showPassword}
                            onToggleVisible={handleClickShowPassword}
                            {...passwordRegister}
                            onBlur={(event) => {
                                setValue('password', event.target.value?.trim(), { shouldValidate: true });
                                void passwordRegister.onBlur(event);
                            }}
                            onChange={(e) => {
                                void passwordRegister.onChange(e);
                                changePassword(e.target.value);
                            }}
                        />
                        {errors.password && <FieldError>{errors.password.message}</FieldError>}
                    </div>
                    <div className="mt-4 flex items-center gap-4">
                        <div className={cn('h-2 w-[85px] rounded-[7px]', level?.color)} />
                        <span className="text-xs leading-[1.375rem] font-semibold">{level?.label}</span>
                    </div>
                </div>
                <div className="flex flex-col gap-2">
                    <Label htmlFor="confirm-password-reset">Confirm New Password</Label>
                    <Input
                        id="confirm-password-reset"
                        type="password"
                        placeholder="Enter confirm password"
                        autoComplete="new-password"
                        aria-invalid={Boolean(errors.confirmPassword)}
                        {...confirmPasswordRegister}
                        onBlur={(event) => {
                            setValue('confirmPassword', event.target.value?.trim(), { shouldValidate: true });
                            void confirmPasswordRegister.onBlur(event);
                        }}
                    />
                    {errors.confirmPassword && <FieldError>{errors.confirmPassword.message}</FieldError>}
                </div>

                {submitError && <FieldError>{submitError}</FieldError>}
                <Button
                    {...buttonStyle({ variant: 'contained', color: 'secondary' }, 'w-full')}
                    disabled={isSubmitting}
                    type="submit"
                >
                    Set password
                </Button>
            </div>
        </form>
    );
};

export default AuthSetPassword;
